/**
 * F7 — Zip export via fflate.
 *
 * Universal fallback for browsers that don't support FSAP write mode.
 * Produces a Blob the caller can hand to `URL.createObjectURL` for a
 * download. Same projection as `toFolder` — built from the same
 * SharePointTree so a vault round-tripped through zip vs folder is
 * byte-equivalent.
 *
 * 2026-05-28 — migrated from JSZip (~95kb gz) to fflate (~3kb gz for
 * the read+write surface we use). fflate's `{path: Uint8Array}` input
 * shape mirrors the structure of our SharePointTree directly.
 */

import { strToU8, zip as fflateZip, type Zippable } from 'fflate';
import { toSharePointTree } from '../../portable-site';
import type { PortableSite } from '../../portable-site';

export interface ExportToZipResult {
  blob: Blob;
  filesWritten: number;
  assetsWritten: number;
  /** Recommended `<a download>` filename derived from the vault source label. */
  filename: string;
}

function zipAsync(input: Zippable): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    fflateZip(input, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });
}

export async function exportToZip(site: PortableSite): Promise<ExportToZipResult> {
  const tree = toSharePointTree(site);
  const input: Zippable = {};
  let filesWritten = 0;
  let assetsWritten = 0;
  for (const f of tree.files) {
    input[f.path] = strToU8(f.body);
    filesWritten++;
  }
  for (const a of tree.assets) {
    input[a.path] = a.bytes;
    assetsWritten++;
  }
  const bytes = await zipAsync(input);
  // Cast to BlobPart — Uint8Array<ArrayBufferLike> isn't structurally
  // an ArrayBuffer-only BlobPart under TS strict mode.
  const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/zip' });
  const safe = (site.sourceLabel || 'vault').replace(/[^a-zA-Z0-9._-]+/g, '-');
  const filename = `${safe}.zip`;
  return { blob, filesWritten, assetsWritten, filename };
}

/**
 * Convenience: trigger a browser download for an `ExportToZipResult`.
 * Tests don't need to call this — they read the blob directly.
 */
export function downloadZip(result: ExportToZipResult): void {
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
